import i18n from "@/i18n";

const BASE = "/dashboard/api";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...init,
    });
  } catch {
    // fetch itself rejected: offline, DNS, the server is down — no response at all.
    throw new ApiError(0, i18n.t("errors.network"));
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, extractErrorMessage(body, res));
  }
  return body as T;
}

/**
 * FastAPI's `detail` is a plain string for a hand-raised HTTPException, but a
 * Pydantic validation failure (a bad request body — e.g. an unknown topic)
 * comes back as an array of `{loc, msg, type}` objects instead. Both are
 * turned into one readable line so the real server error always reaches the
 * user, not just "something went wrong".
 */
function extractErrorMessage(body: unknown, res: Response): string {
  // These statuses have one meaning whatever the server wrote ("Not
  // authenticated", "missing permission x", a stack trace), so say it in words
  // a person can act on. 400 / 409 / 422 keep the server's own explanation —
  // that is the specific thing to fix ("party of 4 exceeds vehicle capacity 6").
  if (res.status === 401) return i18n.t("errors.unauthorized");
  if (res.status === 403) return i18n.t("errors.forbidden");
  if (res.status === 404) return i18n.t("errors.notFound");
  if (res.status >= 500) return i18n.t("errors.server");
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((e) => {
        if (e && typeof e === "object" && "msg" in e) {
          const loc = Array.isArray((e as { loc?: unknown[] }).loc)
            ? (e as { loc: unknown[] }).loc.filter((p) => p !== "body").join(".")
            : undefined;
          const msg = String((e as { msg: unknown }).msg);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(e);
      })
      .join("; ");
  }
  return i18n.t("errors.generic", { status: res.status });
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: "POST", body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: "PATCH", body: data ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  // Multipart upload: pass an empty headers object so the browser sets
  // Content-Type (with the multipart boundary) itself.
  upload: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form, headers: {} }),
};

export function queryString(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
